/**
 * Plockar läsbar text ur mailfiler så att en tråd kan analyseras.
 *
 * .eml är rå MIME: rubrikerna är kodade (=?UTF-8?B?...?=) och brödtexten
 * base64- eller quoted-printable-kodad. Skickar man filen som den är blir
 * det obegripligt för både människa och modell, därför avkodas den här.
 *
 * .msg är Outlooks binära format (OLE/CFB). Det finns inget rimligt sätt att
 * parsa strukturen i webbläsaren, så vi fiskar ut de läsbara textsträngarna —
 * grovt men fullt användbart som underlag.
 */

const MAX_CHARS = 200000;

export async function readEmailFile(file) {
  const name = (file.name || '').toLowerCase();
  if (name.endsWith('.msg')) return textFromMsg(await file.arrayBuffer());
  const raw = await file.text();
  if (name.endsWith('.eml')) return textFromEml(raw);
  if (name.endsWith('.html') || name.endsWith('.htm')) return htmlToText(raw);
  return raw.slice(0, MAX_CHARS);
}

/** Filändelser som går att läsa av. */
export const EMAIL_FILE_ACCEPT = '.eml,.msg,.txt,.md,.html,.htm';

/* ── HTML ────────────────────────────────────────────────────────────────── */

// Namngivna entiteter som faktiskt förekommer i svenska mail.
const HTML_ENTITIES = {
  nbsp: ' ', amp: '&', lt: '<', gt: '>', quot: '"', apos: "'",
  aring: 'å', auml: 'ä', ouml: 'ö', eacute: 'é', egrave: 'è',
  aelig: 'æ', oslash: 'ø', uuml: 'ü', szlig: 'ß',
  ndash: '–', mdash: '—', hellip: '…', bull: '•', middot: '·',
  laquo: '«', raquo: '»', ldquo: '"', rdquo: '"', lsquo: "'", rsquo: "'",
  euro: '€', pound: '£', deg: '°', copy: '©', reg: '®', trade: '™',
};

export function htmlToText(html) {
  return html
    .replace(/<(script|style)[\s\S]*?<\/\1>/gi, '')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/(p|div|tr|li|h[1-6])>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&#x([0-9a-f]+);/gi, (_, h) => String.fromCharCode(parseInt(h, 16)))
    .replace(/&#(\d+);/g, (_, d) => String.fromCharCode(Number(d)))
    .replace(/&(\w+);/g, (whole, name) => {
      const val = HTML_ENTITIES[name.toLowerCase()];
      if (!val) return whole;
      // &Aring; och &aring; skiljer sig bara i skiftläge
      return /^[A-Z]/.test(name) ? val.toUpperCase() : val;
    })
    .replace(/\n{3,}/g, '\n\n')
    .trim()
    .slice(0, MAX_CHARS);
}

/* ── .eml / MIME ─────────────────────────────────────────────────────────── */

function decodeBytes(bytes, charset) {
  const cs = (charset || 'utf-8').toLowerCase();
  try {
    return new TextDecoder(cs).decode(bytes);
  } catch {
    return new TextDecoder('utf-8').decode(bytes);
  }
}

function quotedPrintableToBytes(str) {
  const clean = str.replace(/=\r?\n/g, '');
  const out = [];
  for (let i = 0; i < clean.length; i += 1) {
    if (clean[i] === '=' && /^[0-9A-Fa-f]{2}$/.test(clean.substr(i + 1, 2))) {
      out.push(parseInt(clean.substr(i + 1, 2), 16));
      i += 2;
    } else {
      out.push(clean.charCodeAt(i) & 0xff);
    }
  }
  return new Uint8Array(out);
}

function base64ToBytes(str) {
  const bin = atob(str.replace(/\s+/g, ''));
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i += 1) out[i] = bin.charCodeAt(i);
  return out;
}

/** =?UTF-8?B?...?= / =?ISO-8859-1?Q?...?= i rubrikrader. */
function decodeMimeWords(value) {
  return value.replace(/=\?([^?]+)\?([BbQq])\?([^?]*)\?=/g, (whole, charset, enc, data) => {
    try {
      const bytes = enc.toUpperCase() === 'B'
        ? base64ToBytes(data)
        : quotedPrintableToBytes(data.replace(/_/g, ' '));
      return decodeBytes(bytes, charset);
    } catch {
      return whole;
    }
  });
}

/** Delar upp en MIME-del i rubriker (hopvikta rader) och kropp. */
function splitPart(raw) {
  const sep = raw.search(/\r?\n\r?\n/);
  const headBlock = sep === -1 ? raw : raw.slice(0, sep);
  const body = sep === -1 ? '' : raw.slice(sep).replace(/^\r?\n\r?\n/, '');
  const headers = {};
  headBlock
    .replace(/\r?\n[ \t]+/g, ' ')     // hopvikta fortsättningsrader
    .split(/\r?\n/)
    .forEach(line => {
      const m = line.match(/^([\w-]+):\s*([\s\S]*)$/);
      if (m) headers[m[1].toLowerCase()] = m[2].trim();
    });
  return { headers, body };
}

function decodeBody(headers, body) {
  const enc = (headers['content-transfer-encoding'] || '').toLowerCase();
  const charset = (headers['content-type'] || '').match(/charset="?([\w-]+)"?/i)?.[1];
  if (enc === 'base64') {
    try { return decodeBytes(base64ToBytes(body), charset); } catch { return body; }
  }
  if (enc === 'quoted-printable') return decodeBytes(quotedPrintableToBytes(body), charset);
  return body;
}

/** Brödtexten ur en MIME-del — går ner i multipart och föredrar text/plain. */
function bodyText(headers, body, depth = 0) {
  const contentType = (headers['content-type'] || 'text/plain').toLowerCase();
  const boundary = (headers['content-type'] || '').match(/boundary="?([^";]+)"?/i)?.[1];

  if (contentType.startsWith('multipart/') && boundary && depth < 4) {
    // Delarna avgränsas av "--boundary"; sista gränsen slutar med "--".
    const parts = body.split(`--${boundary}`)
      .slice(1)
      .map(p => p.replace(/^--/, '').replace(/^\r?\n/, ''))
      .filter(p => p.trim());
    const decoded = parts.map(p => {
      const sub = splitPart(p);
      return { type: (sub.headers['content-type'] || '').toLowerCase(), text: bodyText(sub.headers, sub.body, depth + 1) };
    }).filter(p => p.text.trim());
    const plain = decoded.find(p => p.type.startsWith('text/plain'));
    if (plain) return plain.text;
    const html = decoded.find(p => p.type.startsWith('text/html'));
    if (html) return html.text;
    return decoded.map(p => p.text).join('\n\n');
  }

  const decoded = decodeBody(headers, body);
  if (contentType.startsWith('text/html')) return htmlToText(decoded);
  if (contentType.startsWith('text/') || depth === 0) return decoded;
  return ''; // bilagor och inbäddade bilder är brus i sammanhanget
}

export function textFromEml(raw) {
  const { headers, body } = splitPart(raw);
  const head = ['Date', 'From', 'To', 'Cc', 'Subject']
    .map(k => (headers[k.toLowerCase()] ? `${k}: ${decodeMimeWords(headers[k.toLowerCase()])}` : ''))
    .filter(Boolean)
    .join('\n');
  const text = bodyText(headers, body).replace(/\n{3,}/g, '\n\n').trim();
  return `${head}\n\n${text}`.slice(0, MAX_CHARS);
}

/* ── .msg (Outlook) ──────────────────────────────────────────────────────── */

function isPrintable(code) {
  return code === 9 || code === 10 || code === 13 || (code >= 32 && code !== 127);
}

/**
 * Läser ut textsträngar ur en .msg. Outlook lagrar text som UTF-16LE, men
 * transportrubrikerna ligger ofta som ren ASCII — båda plockas upp.
 */
export function textFromMsg(buffer) {
  const bytes = new Uint8Array(buffer);
  const chunks = [];

  const flush = (chars) => {
    const s = chars.join('').replace(/\s+/g, ' ').trim();
    if (s.length >= 12) chunks.push(s);
  };

  // UTF-16LE: läsbart tecken följt av nollbyte
  let utf16 = [];
  for (let i = 0; i + 1 < bytes.length; i += 2) {
    if (bytes[i + 1] === 0 && isPrintable(bytes[i])) {
      utf16.push(String.fromCharCode(bytes[i]));
    } else if (bytes[i + 1] === 0 && bytes[i] === 0) {
      if (utf16.length) { flush(utf16); utf16 = []; }
    } else {
      if (utf16.length) { flush(utf16); utf16 = []; }
      i -= 1; // håll fasen mot udda offset
    }
  }
  if (utf16.length) flush(utf16);

  // ASCII-strängar (rubriker, adresser)
  let ascii = [];
  for (let i = 0; i < bytes.length; i += 1) {
    if (isPrintable(bytes[i]) && bytes[i] < 128) ascii.push(String.fromCharCode(bytes[i]));
    else { if (ascii.length) flush(ascii); ascii = []; }
  }
  if (ascii.length) flush(ascii);

  // Samma sträng dyker upp i flera strömmar — visa den en gång.
  const seen = new Set();
  const unique = chunks.filter(c => (seen.has(c) ? false : seen.add(c)));
  return unique.join('\n').slice(0, MAX_CHARS);
}
